import { Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin';
// Layout components
import Navbar from 'components/navbar/NavbarAdmin';
import Sidebar from 'components/sidebar/Sidebar';
import { SidebarContext } from 'contexts/SidebarContext';
import { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import routes from 'routes';
import { useAuth } from 'contexts/AuthContext';
import { getVisibleSidebarRoutes } from 'utils/routeUtils';
import { UserRole } from 'utils/rolePermissions';

// Custom Chakra theme
export default function AgencyDashboard(props: { [x: string]: any }) {
    const { ...rest } = props;
    const location = useLocation();
    const { user } = useAuth();

    // states and functions
    const [fixed] = useState(false);
    const [toggleSidebar, setToggleSidebar] = useState(false);

    // Filter routes for agency layout only - use proper role-based filtering
    const agencyRoutes = routes.filter(route =>
        route.layout === '/agency'
    );

    // Get visible sidebar routes based on user role
    const visibleSidebarRoutes = getVisibleSidebarRoutes(routes, user?.role);

    // functions for changing the states from components
    const getRoute = () => {
        return location.pathname !== '/agency/full-screen-maps';
    };

    const getActiveRoute = (routes: RoutesType[]): string => {
        const currentPath = location.pathname;
        const searchParams = new URLSearchParams(location.search);

        // Default route name based on context
        let activeRoute = 'Agency Dashboard';

        for (let i = 0; i < routes.length; i++) {
            const route = routes[i];
            const fullPath = route.layout + route.path;

            // Check for exact match first
            if (currentPath === fullPath) {
                return route.name;
            }

            // Handle dynamic routes with parameters
            if (route.path.includes(':')) {
                const routePattern = fullPath.replace(/:[^/]+/g, '([^/]+)');
                const regex = new RegExp(`^${routePattern}$`);
                const match = currentPath.match(regex);

                if (match) {
                    // Special handling for calendar routes
                    if (route.path.includes('/calendar/:clientId')) {
                        const clientName = searchParams.get('client_name');
                        if (clientName) {
                            return `${clientName}'s Calendar`;
                        }
                        return 'Client Calendar';
                    }
                    return route.name;
                }
            }
        }

        // Context-aware fallbacks based on URL patterns
        if (currentPath.includes('/calendar/')) {
            const clientName = searchParams.get('client_name');
            if (clientName) {
                return `${clientName}'s Calendar`;
            }
            return 'Client Calendar';
        }

        if (currentPath.includes('/calendar')) {
            return 'Calendar Overview';
        }

        if (currentPath.includes('/trainers')) {
            return 'Trainer Management';
        }

        return activeRoute;
    };

    const getActiveNavbar = (routes: RoutesType[]): boolean => {
        let activeNavbar = false;
        const currentPath = location.pathname;

        for (let i = 0; i < routes.length; i++) {
            const fullPath = routes[i].layout + routes[i].path;
            if (currentPath === fullPath) {
                return routes[i].secondary ?? false;
            }
        }
        return activeNavbar;
    };

    const getActiveNavbarText = (routes: RoutesType[]): string | boolean => {
        let activeNavbar = false;
        const currentPath = location.pathname;

        for (let i = 0; i < routes.length; i++) {
            const fullPath = routes[i].layout + routes[i].path;
            if (currentPath === fullPath) {
                return routes[i].name;
            }
        }
        return activeNavbar;
    };

    const getRoutes = (routes: RoutesType[]): any => {
        return routes.map((route: RoutesType, key: any) => {
            if (route.layout === '/agency') {
                return (
                    <Route path={`${route.path}`} element={route.component} key={key} />
                );
            } else {
                return null;
            }
        });
    };

    document.documentElement.dir = 'ltr';
    const { onOpen } = useDisclosure();

    return (
        <Box>
            <SidebarContext.Provider
                value={{
                    toggleSidebar,
                    setToggleSidebar,
                }}
            >
                <Sidebar routes={visibleSidebarRoutes} display="none" {...rest} />
                <Box
                    float="right"
                    minHeight="100vh"
                    height="100%"
                    overflow="auto"
                    position="relative"
                    maxHeight="100%"
                    w={{ base: '100%', xl: 'calc( 100% - 290px )' }}
                    maxWidth={{ base: '100%', xl: 'calc( 100% - 290px )' }}
                    transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
                    transitionDuration=".2s, .2s, .35s"
                    transitionProperty="top, bottom, width"
                    transitionTimingFunction="linear, linear, ease"
                >
                    <Portal>
                        <Box>
                            <Navbar
                                onOpen={onOpen}
                                logoText={'Agency Dashboard'}
                                brandText={getActiveRoute(visibleSidebarRoutes)}
                                secondary={getActiveNavbar(visibleSidebarRoutes)}
                                message={getActiveNavbarText(visibleSidebarRoutes)}
                                fixed={fixed}
                                {...rest}
                            />
                        </Box>
                    </Portal>

                    {getRoute() ? (
                        <Box
                            mx="auto"
                            p={{ base: '20px', md: '30px' }}
                            pe="20px"
                            minH="100vh"
                            pt="50px"
                        >
                            <Routes>
                                {getRoutes(agencyRoutes)}
                                <Route
                                    path="/"
                                    element={<Navigate to="/agency/default" replace />}
                                />
                                {/* 404 route for any unmatched agency routes */}
                                <Route path="*" element={<Navigate to="/404" replace />} />
                            </Routes>
                        </Box>
                    ) : null}
                    <Box>
                        <Footer />
                    </Box>
                </Box>
            </SidebarContext.Provider>
        </Box>
    );
}
